import { useEffect, useState, useCallback } from 'react';
import {
  Button,
  SpaceBetween,
  Table,
  Modal,
  Box,
  FormField,
  Input,
  Header,
  TextFilter,
  Pagination,
  CollectionPreferences,
} from '@cloudscape-design/components';
import { useAuth } from 'react-oidc-context';
import { getEndpoint } from '../../utils';

interface Section {
  PK: string;
  SK: string;
  name?: string;
  description: string;
  order: number;
}

export function SectionsTable() {
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSections, setSelectedSections] = useState<Section[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [fetchSectionsFails, setFetchSectionsFails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filteringText, setFilteringText] = useState("");
  const [preferences, setPreferences] = useState({ pageSize: 10 });
  const [pageIndex, setPageIndex] = useState(1);
  const auth = useAuth();

  const fetchSections = useCallback(async () => {
    if (auth.isAuthenticated) {
      setIsLoading(true);
      try {
        const response = await fetch(getEndpoint('/api/admin/sections'), {
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSections(data);
      } catch (error) {
        console.error('Error fetching sections:', error);
        setFetchSectionsFails(true);
      } finally {
        setIsLoading(false);
      }
    }
  }, [auth.isAuthenticated, auth.user?.id_token, setSections, setIsLoading, setFetchSectionsFails]);

  const handleDelete = useCallback(async () => {
    if (selectedSections.length > 0) {
      try {
        await Promise.all(
          selectedSections.map((section) =>
            fetch(getEndpoint(`/api/admin/sections/${section.PK.replace('FAQ#', '')}`), {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${auth.user?.access_token}`,
              },
            })
          )
        );
        await fetchSections();
        setSelectedSections([]);
        setShowDeleteModal(false);
      } catch (error) {
        console.error('Error deleting sections:', error);
      }
    }
  }, [selectedSections, auth.user?.access_token, fetchSections]);

  const handleEdit = useCallback(async () => {
    if (editingSection) {
      try {
        if (editingSection.PK === 'FAQ#') {
          // This is a new section
          await fetch(getEndpoint('/api/admin/sections'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
            body: JSON.stringify({
              section: editingSection.name,
              description: editingSection.description,
              order: editingSection.order,
            }),
          });
        } else {
          // This is an existing section
          const sectionName = editingSection.PK.replace('FAQ#', '');
          await fetch(getEndpoint(`/api/admin/sections/${sectionName}`), {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
            body: JSON.stringify({
              description: editingSection.description,
              order: editingSection.order,
            }),
          });
        }
        await fetchSections();
        setShowEditModal(false);
        setEditingSection(null);
      } catch (error) {
        console.error('Error updating section:', error);
      }
    }
  }, [editingSection, auth.user?.id_token, fetchSections, setShowEditModal, setEditingSection]);

  useEffect(() => {
    if (!fetchSectionsFails) {
      fetchSections();
    }
  }, [fetchSections, fetchSectionsFails]);

  const filteredSections = sections.filter(
    (section) =>
      section.name?.toLowerCase().includes(filteringText.toLowerCase()) ||
      section.description?.toLowerCase().includes(filteringText.toLowerCase())
  );

  const startIndex = (pageIndex - 1) * preferences.pageSize;
  const endIndex = startIndex + preferences.pageSize;
  const currentPageSections = filteredSections.slice(startIndex, endIndex);

  return (
    <SpaceBetween direction="vertical" size="l">
      <Header
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => {
                setEditingSection({
                  PK: 'FAQ#',
                  SK: 'SECTION',
                  name: '',
                  description: '',
                  order: 999
                });
                setShowEditModal(true);
              }}>
                Add Section
              </Button>
              <Button
                disabled={selectedSections.length !== 1}
                onClick={() => {
                  setEditingSection(selectedSections[0]);
                  setShowEditModal(true);
                }}
              >
                Edit
              </Button>
              <Button
                disabled={selectedSections.length === 0}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        }
      >
        FAQ Sections
      </Header>
      <Table
        loading={isLoading}
        loadingText="Loading sections..."
        items={currentPageSections}
        selectedItems={selectedSections}
        onSelectionChange={({ detail }) => setSelectedSections(detail.selectedItems)}
        selectionType="multi"
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item) => item.PK.replace('FAQ#', ''),
          },
          {
            id: 'description',
            header: 'Description',
            cell: (item) => item.description,
          },
          {
            id: 'order',
            header: 'Order',
            cell: (item) => item.order,
          },
        ]}
        filter={
          <TextFilter
            filteringPlaceholder="Search sections"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setPageIndex(1);
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            pageSizePreference={{
              title: "Select page size",
              options: [
                { value: 5, label: "5" },
                { value: 10, label: "10" },
                { value: 25, label: "25" },
              ]
            }}
            preferences={{ pageSize: preferences.pageSize }}
            onConfirm={({ detail }) => {
              setPreferences({...preferences, pageSize: detail.pageSize ?? 10 });
              setPageIndex(1);
            }}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={pageIndex}
            pagesCount={Math.ceil(filteredSections.length / preferences.pageSize)}
            onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
          />
        }
      />

      <Modal
        visible={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        header="Delete Sections"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to delete the selected sections?
      </Modal>

      <Modal
        visible={showEditModal}
        onDismiss={() => {
          setShowEditModal(false);
          setEditingSection(null);
        }}
        header="Edit Section"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSection(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleEdit}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editingSection && (
          <SpaceBetween direction="vertical" size="l">
            {editingSection.PK === 'FAQ#' && (
              <FormField label="Section Name">
                <Input
                  value={editingSection.name || ''}
                  onChange={({ detail }) =>
                    setEditingSection({
                      ...editingSection,
                      name: detail.value,
                    })
                  }
                />
              </FormField>
            )}
            <FormField label="Description">
              <Input
                value={editingSection.description}
                onChange={({ detail }) =>
                  setEditingSection({
                    ...editingSection,
                    description: detail.value,
                  })
                }
              />
            </FormField>
            <FormField label="Order">
              <Input
                type="number"
                value={editingSection.order.toString()}
                onChange={({ detail }) =>
                  setEditingSection({
                    ...editingSection,
                    order: parseInt(detail.value) || 999,
                  })
                }
              />
            </FormField>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
